import './style.css'
import {
  createInitialGameState,
  endTurn,
  startNewGame,
  type GameState,
} from './core/gameState'
import { getOpponentMajorPowerId } from './core/strategicAi'
import {
  appendStrategicEvents,
  type StrategicEvent,
} from './core/strategicLog'
import {
  getAiDifficultyLabel,
  isAiDifficulty,
  type AiDifficulty,
} from './core/gameSettings'
import {
  getConstructionAvailability,
  getOccupiedSlots,
  PROTOTYPE_CONSTRUCTION_CAP,
  queueFacilityConstruction,
  type ConstructionBlockReason,
} from './core/construction'
import {
  getResearchAvailability,
  startResearch,
  type ResearchBlockReason,
} from './core/research'
import {
  getNationMissionPreparationTurns,
  getSpaceMissionAvailability,
  startSpaceMission,
  type SpaceMissionBlockReason,
} from './core/spaceProgram'
import { GAME_VERSION, PROJECT_PHASE } from './core/version'
import { getNationPowerStatus } from './core/power'
import {
  FACILITY_DEFINITIONS,
  getFacilityDefinition,
  type FacilityId,
} from './data/facilities'
import {
  SPACE_MISSION_DEFINITIONS,
  getSpaceMissionDefinition,
  isSpaceMissionId,
  type SpaceMissionId,
} from './data/spaceMissions'
import {
  TECHNOLOGY_DEFINITIONS,
  getTechnologyBranchLabel,
  getTechnologyDefinition,
  isTechnologyId,
  type TechnologyId,
} from './data/technologies'
import {
  getPatronName,
  getPrototypeRegion,
  type ActiveMajorPowerId,
  type RegionDefinition,
} from './data/regions'
import {
  WORLD_MAP_REGIONS,
  getWorldMapRegion,
  getWorldMapSphereLabel,
  type WorldMapRegionDefinition,
} from './data/worldMap'
import { getNationProfile } from './data/nationProfiles'
import {
  isMajorPowerId,
  type MajorPowerId,
} from './data/nations'
import {
  getNationArchiveEntries,
  getNationArchiveEntry,
} from './data/nationArchive'
import { downloadGameSave, readGameSaveFile } from './storage/browserFiles'
import {
  loadAutosave,
  saveAutosave,
} from './storage/saveState'

const appNode = document.querySelector<HTMLElement>('#app')

if (!appNode) {
  throw new Error('Missing #app root element')
}

const app: HTMLElement = appNode

let state: GameState = createInitialGameState()
let notice = '尚未开始原型局。'
let noticeTone: 'neutral' | 'success' | 'error' = 'neutral'
let endTurnLocked = false
let selectedRegionId = 'star-west'
let selectedStartNationId: ActiveMajorPowerId = 'starsea'
let selectedAiDifficulty: AiDifficulty = 'standard'
let selectedArchiveNationId: MajorPowerId = 'starsea'

type GameView =
  | 'overview'
  | 'map'
  | 'research'
  | 'space'
  | 'opponent'
  | 'log'
  | 'nations'
  | 'settings'

const GAME_VIEWS: GameView[] = [
  'overview',
  'map',
  'research',
  'space',
  'opponent',
  'log',
  'nations',
  'settings',
]

let activeView: GameView = 'map'

restoreAutosave()
render()

function restoreAutosave(): void {
  try {
    const result = loadAutosave(window.localStorage)

    if (result.kind === 'loaded') {
      state = result.state
      selectedStartNationId = state.playerNationId ?? 'starsea'
      selectedAiDifficulty = state.settings.aiDifficulty

      if (result.migrated) {
        persistState(
          `旧版存档已自动升级到国家路线与战略记录系统，当前仍为第 ${state.turn} 回合。`,
        )
      } else {
        notice = `已从本机自动存档恢复到第 ${state.turn} 回合。`
        noticeTone = 'success'
      }

      return
    }

    if (result.kind === 'invalid') {
      notice = `检测到损坏或不兼容的自动存档：${result.error}`
      noticeTone = 'error'
    }
  } catch {
    notice = '当前浏览器无法读取本地存储，仍可试玩，但自动存档不可用。'
    noticeTone = 'error'
  }
}

function persistState(successMessage: string): void {
  try {
    saveAutosave(window.localStorage, state)
    notice = successMessage
    noticeTone = 'success'
  } catch {
    notice = '操作已完成，但浏览器拒绝写入本地存储；建议导出存档。'
    noticeTone = 'error'
  }
}

function getViewedNationId(): ActiveMajorPowerId {
  return state.playerNationId ?? 'starsea'
}

function renderResearch(): string {
  const nationId = getViewedNationId()
  const nationResearch = state.research[nationId]
  const researchCapacity = state.nations[nationId].economy.capacity.research
  const active = nationResearch.active
  const activeDefinition = active
    ? getTechnologyDefinition(active.technologyId)
    : null

  return `
    <section class="research-panel" aria-labelledby="research-title">
      <div class="research-heading">
        <div>
          <p class="kicker">TECHNOLOGY PROGRAM</p>
          <h2 id="research-title">国家科技树</h2>
        </div>
        <div class="research-overview">
          <span>科研能力 <strong>${researchCapacity}/回合</strong></span>
          <span>已完成 <strong>${nationResearch.completed.length}/${TECHNOLOGY_DEFINITIONS.length}</strong></span>
        </div>
      </div>

      <div class="research-active">
        ${active && activeDefinition
          ? `
            <div>
              <span>当前研究</span>
              <strong>${activeDefinition.name}</strong>
            </div>
            <div class="research-progress-block">
              <span>${active.progress} / ${activeDefinition.researchCost}</span>
              <div class="research-progress-track" aria-label="${activeDefinition.name}研究进度">
                <i style="width: ${Math.min(100, (active.progress / activeDefinition.researchCost) * 100)}%"></i>
              </div>
            </div>
          `
          : `
            <div>
              <span>当前研究</span>
              <strong>暂无项目</strong>
            </div>
            <small>选择一项可研究科技开始项目；每个国家同时只能研究一项。</small>
          `}
      </div>

      <div class="technology-tree">
        ${[1, 2, 3, 4].map((tier) => renderTechnologyTier(tier as 1 | 2 | 3 | 4)).join('')}
      </div>
    </section>
  `
}

function renderTechnologyTier(tier: 1 | 2 | 3 | 4): string {
  const nationId = getViewedNationId()
  const nationResearch = state.research[nationId]
  const technologies = TECHNOLOGY_DEFINITIONS.filter(
    (technology) => technology.tier === tier,
  )

  return `
    <section class="tech-tier">
      <div class="tech-tier-heading">
        <span>阶段 ${tier}</span>
        <i></i>
      </div>
      <div class="tech-grid">
        ${technologies.map((technology) => {
          const completed = nationResearch.completed.includes(technology.id)
          const isActive =
            nationResearch.active?.technologyId === technology.id
          const availability = getResearchAvailability(
            nationResearch,
            technology.id,
          )
          const gameRunning = state.status === 'running'
          const canStart = gameRunning && availability.allowed
          const prerequisites = technology.prerequisites
            .map((technologyId) => getTechnologyDefinition(technologyId).name)
            .join('、')
          const status = technologyStatusText(
            technology.id,
            gameRunning,
            completed,
            isActive,
            availability.reason,
            availability.missingPrerequisites,
          )

          return `
            <article class="tech-card branch-${technology.branch} ${completed ? 'is-completed' : ''} ${isActive ? 'is-active' : ''}">
              <div class="tech-card-topline">
                <span>${getTechnologyBranchLabel(technology.branch)}</span>
                <b>${technology.researchCost} 点</b>
              </div>
              <h3>${technology.name}</h3>
              <p>${technology.description}</p>
              <small class="tech-prerequisites">
                前置：${prerequisites || '无'}
              </small>
              <small class="tech-unlock">${technology.unlockHint}</small>
              <button
                type="button"
                data-research-tech="${technology.id}"
                ${canStart ? '' : 'disabled'}
              >
                ${status}
              </button>
            </article>
          `
        }).join('')}
      </div>
    </section>
  `
}

function technologyStatusText(
  technologyId: TechnologyId,
  gameRunning: boolean,
  completed: boolean,
  isActive: boolean,
  reason: ResearchBlockReason | undefined,
  missingPrerequisites: TechnologyId[],
): string {
  if (completed) {
    return '已完成'
  }

  if (isActive) {
    return '研究中'
  }

  if (!gameRunning) {
    return '请先开始原型局'
  }

  if (reason === 'project-active') {
    return '已有研究项目'
  }

  if (reason === 'missing-prerequisites') {
    const names = missingPrerequisites
      .map((technologyId) => getTechnologyDefinition(technologyId).name)
      .join('、')
    return `缺少：${names}`
  }

  if (reason === 'already-completed') {
    return '已完成'
  }

  if (reason === 'already-researching') {
    return '研究中'
  }

  const definition = getTechnologyDefinition(technologyId)
  return `开始研究 · ${definition.researchCost} 点`
}


function renderSpaceProgram(): string {
  const nationId = getViewedNationId()
  const nation = state.nations[nationId]
  const program = state.spaceProgram[nationId]
  const active = program.active

  return `
    <section class="space-program-panel" aria-labelledby="space-program-title">
      <div class="space-program-heading">
        <div>
          <p class="kicker">SPACE PROGRAM</p>
          <h2 id="space-program-title">国家航天计划</h2>
        </div>
        <div class="space-program-overview">
          <span>里程碑 <strong>${program.completed.length}/${SPACE_MISSION_DEFINITIONS.length}</strong></span>
          <span>状态 <strong>${active ? '任务准备中' : '待命'}</strong></span>
        </div>
      </div>

      ${state.winnerNationId === null
        ? ''
        : `<div class="space-victory ${state.status === 'won' ? 'is-win' : 'is-loss'}">
            <strong>${getPatronName(state.winnerNationId)}完成载人登月并安全返回</strong>
            <span>${state.status === 'won' ? '航天胜利已达成。' : '对手率先完成航天胜利目标。'}</span>
          </div>`}

      <div class="mission-grid">
        ${SPACE_MISSION_DEFINITIONS.map((mission) => {
          const completed = program.completed.includes(mission.id)
          const isActive = active?.missionId === mission.id
          const availability = getSpaceMissionAvailability(
            program,
            state.research[nationId],
            nation.economy.stockpile,
            mission.id,
            state.status === 'running',
          )
          const requiredTechs = mission.requiredTechnologies
            .map((technologyId) => getTechnologyDefinition(technologyId).name)
            .join('、')
          const prerequisite = mission.prerequisiteMission
            ? getSpaceMissionDefinition(mission.prerequisiteMission).shortName
            : '无'
          const status = spaceMissionStatusText(
            mission.id,
            completed,
            isActive,
            availability.reason,
            availability.missingTechnologies,
            availability.missingMilestone,
          )

          return `
            <article class="mission-card ${completed ? 'is-completed' : ''} ${isActive ? 'is-active' : ''}">
              <div class="mission-order">0${mission.order}</div>
              <div class="mission-card-heading">
                <div>
                  <span>航天里程碑</span>
                  <h3>${mission.name}</h3>
                </div>
                <b>${getNationMissionPreparationTurns(nationId, mission.id)} 回合</b>
              </div>
              <p>${mission.description}</p>
              <div class="mission-requirements">
                <span>科技：${requiredTechs || '无'}</span>
                <span>前置任务：${prerequisite}</span>
              </div>
              <small class="mission-cost">
                资金 ${mission.cost.money} · 金属 ${mission.cost.metal} · 燃料 ${mission.cost.fuel} · 电子 ${mission.cost.electronics}
              </small>
              ${isActive && active
                ? `<div class="mission-progress">
                    <span>准备进度：剩余 ${active.turnsRemaining}/${active.totalTurns} 回合</span>
                    <div><i style="width: ${Math.max(0, ((active.totalTurns - active.turnsRemaining) / active.totalTurns) * 100)}%"></i></div>
                  </div>`
                : ''}
              <button
                type="button"
                data-space-mission="${mission.id}"
                ${availability.allowed ? '' : 'disabled'}
              >
                ${status}
              </button>
            </article>
          `
        }).join('')}
      </div>
    </section>
  `
}

function spaceMissionStatusText(
  missionId: SpaceMissionId,
  completed: boolean,
  isActive: boolean,
  reason: SpaceMissionBlockReason | undefined,
  missingTechnologies: string[],
  missingMilestone: SpaceMissionId | null,
): string {
  if (completed) {
    return '已完成'
  }

  if (isActive) {
    return '任务准备中'
  }

  if (reason === 'game-not-running') {
    return '请先开始原型局'
  }

  if (reason === 'mission-active') {
    return '已有航天任务'
  }

  if (reason === 'missing-milestone' && missingMilestone) {
    return `需先完成：${getSpaceMissionDefinition(missingMilestone).shortName}`
  }

  if (reason === 'missing-technologies') {
    const names = missingTechnologies
      .map((technologyId) => getTechnologyDefinition(technologyId as TechnologyId).name)
      .join('、')
    return `缺少科技：${names}`
  }

  if (reason === 'insufficient-resources') {
    return '资源不足'
  }

  if (reason === 'already-completed') {
    return '已完成'
  }

  return `启动：${getSpaceMissionDefinition(missionId).shortName}`
}

function spaceMissionReasonText(
  reason: SpaceMissionBlockReason,
  missingTechnologies: string[],
  missingMilestone: SpaceMissionId | null,
): string {
  if (reason === 'game-not-running') {
    return '请先开始原型局。'
  }

  if (reason === 'mission-active') {
    return '当前已有航天任务正在准备。'
  }

  if (reason === 'missing-milestone' && missingMilestone) {
    return `需先完成航天里程碑：${getSpaceMissionDefinition(missingMilestone).name}。`
  }

  if (reason === 'missing-technologies') {
    const names = missingTechnologies
      .map((technologyId) => getTechnologyDefinition(technologyId as TechnologyId).name)
      .join('、')
    return `缺少科技：${names}。`
  }

  if (reason === 'insufficient-resources') {
    return '当前资源不足以启动该航天任务。'
  }

  return '该航天里程碑已经完成。'
}

function renderOpponentIntel(): string {
  const opponentId = getOpponentMajorPowerId(state.playerNationId)

  if (opponentId === null) {
    return ''
  }

  const opponent = state.nations[opponentId]
  const research = state.research[opponentId]
  const program = state.spaceProgram[opponentId]
  const projects = state.construction.queue.filter(
    (project) => project.nationId === opponentId,
  )
  const activeResearch = research.active
    ? getTechnologyDefinition(research.active.technologyId).name
    : '暂无'
  const activeMission = program.active
    ? getSpaceMissionDefinition(program.active.missionId).shortName
    : '暂无'

  return `
    <section class="opponent-panel" aria-labelledby="opponent-title">
      <div class="opponent-heading">
        <div>
          <p class="kicker">RIVAL STRATEGIC AI</p>
          <h2 id="opponent-title">对手战略情报 · ${getPatronName(opponentId)}</h2>
        </div>
        <span>${getAiDifficultyLabel(state.settings.aiDifficulty)} · AI v2</span>
      </div>

      <div class="opponent-grid">
        <article>
          <span>当前科研</span>
          <strong>${activeResearch}</strong>
          <small>${research.active ? `${research.active.progress} 点进度` : `已完成 ${research.completed.length} 项`}</small>
        </article>
        <article>
          <span>工业建设</span>
          <strong>${projects.length}/${PROTOTYPE_CONSTRUCTION_CAP} 项施工中</strong>
          <small>${projects.length === 0
            ? '暂无工程'
            : projects.map((project) =>
                `${getFacilityDefinition(project.facilityId).name}(${project.turnsRemaining})`,
              ).join(' · ')}</small>
        </article>
        <article>
          <span>航天计划</span>
          <strong>${activeMission}</strong>
          <small>里程碑 ${program.completed.length}/${SPACE_MISSION_DEFINITIONS.length}</small>
        </article>
        <article>
          <span>资源库存</span>
          <strong>${opponent.economy.stockpile.money} / ${opponent.economy.stockpile.metal} / ${opponent.economy.stockpile.fuel} / ${opponent.economy.stockpile.electronics}</strong>
          <small>资金 / 金属 / 燃料 / 电子</small>
        </article>
      </div>

      <p class="opponent-note">
        开发阶段暂时公开对手内部状态，方便验证 AI。后续可改为有限情报与侦察系统。
      </p>
    </section>
  `
}

function renderStrategicLog(): string {
  const entries = [...state.strategicLog.entries].reverse().slice(0, 12)

  return `
    <section class="strategic-log-panel" aria-labelledby="strategic-log-title">
      <div class="strategic-log-heading">
        <div>
          <p class="kicker">STRATEGIC EVENT LOG</p>
          <h2 id="strategic-log-title">战略事件记录</h2>
        </div>
        <span>最近 ${entries.length} 条</span>
      </div>
      ${entries.length === 0
        ? '<p class="strategic-log-empty">暂无战略事件。开始科研、建设或推进回合后会记录在这里。</p>'
        : `<div class="strategic-log-list">
            ${entries.map((event) => {
              const sideClass =
                event.nationId === state.playerNationId
                  ? 'is-player'
                  : 'is-rival'
              return `
                <article class="${sideClass}">
                  <span>第 ${event.turn} 回合 · ${getPatronName(event.nationId)}</span>
                  <strong>${formatStrategicEvent(event)}</strong>
                </article>
              `
            }).join('')}
          </div>`}
    </section>
  `
}

function formatStrategicEvent(event: StrategicEvent): string {
  if (event.type === 'research-start') {
    return `开始研究：${getTechnologyDefinition(event.technologyId).name}`
  }

  if (event.type === 'research-complete') {
    return `科技完成：${getTechnologyDefinition(event.technologyId).name}`
  }

  if (event.type === 'construction-start') {
    const region = getPrototypeRegion(event.regionId)
    return `${region?.name ?? event.regionId}开工：${getFacilityDefinition(event.facilityId).name}`
  }

  if (event.type === 'construction-complete') {
    const region = getPrototypeRegion(event.regionId)
    return `${region?.name ?? event.regionId}完工：${getFacilityDefinition(event.facilityId).name}`
  }

  if (event.type === 'space-mission-start') {
    return `航天任务启动：${getSpaceMissionDefinition(event.missionId).name}`
  }

  if (event.type === 'space-mission-complete') {
    return `航天里程碑完成：${getSpaceMissionDefinition(event.missionId).name}`
  }

  return '完成载人登月并安全返回，赢得航天竞赛'
}

function fitMapResourceLabel(label: string, width: number, inset: number, fontSize: number): string {
  const characters = Array.from(label)
  const maxCharacters = Math.floor((width - inset * 2) / fontSize)
  return characters.length <= maxCharacters
    ? label
    : `${characters.slice(0, maxCharacters - 1).join('')}…`
}

function renderMap(): string {
  const adjacencyLines = WORLD_MAP_REGIONS.flatMap((region) =>
    region.neighbors
      .filter((neighborId) => region.id < neighborId)
      .map((neighborId) => {
        const neighbor = getWorldMapRegion(neighborId)

        if (!neighbor) {
          return ''
        }

        const x1 = region.x + region.width / 2
        const y1 = region.y + region.height / 2
        const x2 = neighbor.x + neighbor.width / 2
        const y2 = neighbor.y + neighbor.height / 2
        const plannedClass =
          region.status === 'planned' || neighbor.status === 'planned'
            ? ' route-line-planned'
            : ''

        return `<line class="route-line${plannedClass}" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" />`
      }),
  ).join('')

  const regions = WORLD_MAP_REGIONS.map((region) => {
    const selected = region.id === selectedRegionId
    const runtimeRegion = region.runtimeRegionId
      ? getPrototypeRegion(region.runtimeRegionId) ?? null
      : null
    const controller =
      runtimeRegion === null
        ? null
        : state.regionControllers[runtimeRegion.id] ?? runtimeRegion.patronId
    const sphereId = controller ?? region.sphereId
    const sideClass =
      sphereId === 'unaligned'
        ? 'region-neutral'
        : `region-${sphereId}`
    const relationClass =
      region.role === 'client-state'
        ? 'region-client'
        : region.role === 'minor-state'
          ? 'region-minor'
          : ''
    const plannedClass =
      region.status === 'planned' ? 'region-planned' : ''
    const occupied =
      runtimeRegion === null
        ? 0
        : getOccupiedSlots(state.construction, runtimeRegion.id)
    const footer =
      region.status === 'active'
        ? `槽位 ${occupied}/${region.slots}`
        : '未来剧本'
    const textSize = region.height <= 62 ? 'compact' : region.height <= 74 ? 'medium' : 'regular'
    const textLayout =
      textSize === 'compact'
        ? { inset: 10, nameY: 20, resourceY: 37, footerInset: 7, resourceFontSize: 10.5 }
        : textSize === 'medium'
          ? { inset: 12, nameY: 23, resourceY: 43, footerInset: 9, resourceFontSize: 11 }
          : { inset: 12, nameY: 25, resourceY: 48, footerInset: 10, resourceFontSize: 12 }
    const resourceLabel = fitMapResourceLabel(
      region.resourceLabel,
      region.width,
      textLayout.inset,
      textLayout.resourceFontSize,
    )

    return `
      <g
        class="map-region region-text-${textSize} ${sideClass} ${relationClass} ${plannedClass} ${selected ? 'is-selected' : ''}"
        data-region-id="${region.id}"
        role="button"
        tabindex="0"
        aria-label="${region.name}，${region.ownerName}，${region.status === 'active' ? '当前剧本区域' : '未来规划区域'}，资源倾向${region.resourceLabel}"
      >
        <rect
          x="${region.x}"
          y="${region.y}"
          width="${region.width}"
          height="${region.height}"
          rx="${region.role === 'major-core' ? 16 : 26}"
        />
        <text class="region-name" x="${region.x + textLayout.inset}" y="${region.y + textLayout.nameY}">
          ${region.name}
        </text>
        <text class="region-resource" x="${region.x + textLayout.inset}" y="${region.y + textLayout.resourceY}">
          ${resourceLabel}
        </text>
        <text class="region-slots" x="${region.x + textLayout.inset}" y="${region.y + region.height - textLayout.footerInset}">
          ${footer}
        </text>
      </g>
    `
  }).join('')

  return `
    <svg
      class="prototype-map world-strategy-map"
      viewBox="0 0 1700 600"
      preserveAspectRatio="xMidYMid meet"
      role="group"
      aria-label="五大国与小国规划世界战略地图"
    >
      <text class="map-side-label" x="45" y="36">当前两国剧本 + 五国世界规划层</text>
      <text class="map-side-label map-side-label-right" x="1160" y="36">虚线区域尚未进入回合结算</text>
      ${adjacencyLines}
      ${regions}
    </svg>
  `
}

function renderRegionDetails(region: WorldMapRegionDefinition): string {
  const runtimeRegion = region.runtimeRegionId
    ? getPrototypeRegion(region.runtimeRegionId) ?? null
    : null

  if (runtimeRegion === null) {
    const relationLabel =
      region.role === 'major-core'
        ? '大国核心规划区'
        : region.role === 'client-state'
          ? '未来附属国'
          : '未来独立小国'
    const patronLabel =
      region.patronId === null
        ? '无直接宗主'
        : getPatronName(region.patronId)

    return `
      <aside class="region-details planned-region-details" aria-live="polite">
        <p class="kicker">WORLD REGION PLAN</p>
        <h3>${region.name}</h3>
        <span class="planned-region-badge">未来剧本 · 尚未激活</span>
        <dl>
          <div><dt>名义所属</dt><dd>${region.ownerName}</dd></div>
          <div><dt>地位</dt><dd>${relationLabel}</dd></div>
          <div><dt>所属势力圈</dt><dd>${getWorldMapSphereLabel(region.sphereId)}</dd></div>
          <div><dt>宗主关系</dt><dd>${patronLabel}</dd></div>
          <div><dt>地形</dt><dd>${region.terrain}</dd></div>
          <div><dt>资源侧重</dt><dd>${region.resourceLabel}</dd></div>
        </dl>

        <div class="region-output">
          <strong>未来剧本基础产出预览</strong>
          <span>资金 +${region.production.money}</span>
          <span>金属 +${region.production.metal}</span>
          <span>燃料 +${region.production.fuel}</span>
          <span>电子 +${region.production.electronics}</span>
          <span>电力 ${region.capacity.power}</span>
          <span>科研 ${region.capacity.research}</span>
        </div>

        <div class="planned-region-note">
          <strong>当前仅用于世界格局与资源规划</strong>
          <span>该地区不会参与现有两国的生产、建设、AI 或航天回合；战争与外交也尚未启用。</span>
        </div>
      </aside>
    `
  }

  const roleLabel =
    runtimeRegion.role === 'major-core' ? '大国核心地区' : '附属国'
  const controller =
    state.regionControllers[runtimeRegion.id] ?? runtimeRegion.patronId
  const controllerLabel = getPatronName(controller)
  const occupied = getOccupiedSlots(state.construction, runtimeRegion.id)
  const completed = state.construction.facilities[runtimeRegion.id] ?? []
  const pending = state.construction.queue.filter(
    (project) => project.regionId === runtimeRegion.id,
  )

  return `
    <aside class="region-details" aria-live="polite">
      <p class="kicker">REGION DEVELOPMENT</p>
      <h3>${runtimeRegion.name}</h3>
      <dl>
        <div><dt>名义所属</dt><dd>${runtimeRegion.ownerName}</dd></div>
        <div><dt>地位</dt><dd>${roleLabel}</dd></div>
        <div><dt>当前控制</dt><dd>${controllerLabel}</dd></div>
        <div><dt>地形</dt><dd>${runtimeRegion.terrain}</dd></div>
        <div><dt>资源侧重</dt><dd>${runtimeRegion.resourceLabel}</dd></div>
        <div><dt>建筑槽位</dt><dd>${occupied}/${runtimeRegion.slots}</dd></div>
      </dl>

      <div class="region-output">
        <strong>地区基础产出</strong>
        <span>资金 +${runtimeRegion.production.money}</span>
        <span>金属 +${runtimeRegion.production.metal}</span>
        <span>燃料 +${runtimeRegion.production.fuel}</span>
        <span>电子 +${runtimeRegion.production.electronics}</span>
        <span>电力 ${runtimeRegion.capacity.power}</span>
        <span>科研 ${runtimeRegion.capacity.research}</span>
      </div>

      <div class="facility-status">
        <strong>已建设施</strong>
        <div class="facility-chip-list">
          ${completed.length === 0
            ? '<span class="muted-chip">暂无</span>'
            : completed.map((facilityId) =>
                `<span>${getFacilityDefinition(facilityId).name}</span>`,
              ).join('')}
        </div>
        ${pending.length === 0
          ? ''
          : `<div class="pending-list">${pending.map((project) =>
              `<span>施工中：${getFacilityDefinition(project.facilityId).name} · ${project.turnsRemaining} 回合</span>`,
            ).join('')}</div>`}
      </div>

      ${renderBuildOptions(runtimeRegion, controller)}
    </aside>
  `
}

function renderBuildOptions(
  region: RegionDefinition,
  controller: ActiveMajorPowerId,
): string {
  const nationId = state.playerNationId

  if (nationId === null || controller !== nationId) {
    return `
      <div class="build-section">
        <strong>建设</strong>
        <p>只有玩家控制的地区可以下达建设命令。</p>
      </div>
    `
  }

  return `
    <div class="build-section">
      <strong>建设</strong>
      <div class="build-list">
        ${FACILITY_DEFINITIONS.map((facility) => {
          const availability = getConstructionAvailability(
            state,
            nationId,
            region.id,
            facility.id,
          )
          const disabledReason = availability.reason
            ? constructionReasonText(availability.reason)
            : ''

          return `
            <button
              class="build-option"
              type="button"
              data-build-facility="${facility.id}"
              ${availability.allowed ? '' : 'disabled'}
              title="${disabledReason}"
            >
              <span class="build-option-heading">
                <b>${facility.name}</b>
                <em>${facility.buildTurns} 回合</em>
              </span>
              <small>${facility.shortDescription}</small>
              <small class="build-cost">
                资金 ${facility.cost.money} · 金属 ${facility.cost.metal}
                ${facility.cost.electronics > 0 ? ` · 电子 ${facility.cost.electronics}` : ''}
                ${facility.powerDemand > 0 ? ` · 电力负载 +${facility.powerDemand}` : ' · 电力容量 +5'}
              </small>
              ${disabledReason ? `<small class="build-blocked">${disabledReason}</small>` : ''}
            </button>
          `
        }).join('')}
      </div>
    </div>
  `
}

function constructionReasonText(reason: ConstructionBlockReason): string {
  const labels: Record<ConstructionBlockReason, string> = {
    'game-not-running': '请先开始原型局',
    'region-not-found': '地区数据无效',
    'not-controlled': '当前未控制该地区',
    'no-slots': '地区建筑槽位已满',
    'queue-full': '国家施工队列已满',
    'insufficient-power': '电力容量不足，请先建设发电站',
    'insufficient-resources': '资源不足',
  }

  return labels[reason]
}

function researchReasonText(
  reason: ResearchBlockReason,
  missingPrerequisites: TechnologyId[],
): string {
  if (reason === 'project-active') {
    return '当前已有研究项目，请先完成当前研究。'
  }

  if (reason === 'missing-prerequisites') {
    const names = missingPrerequisites
      .map((technologyId) => getTechnologyDefinition(technologyId).name)
      .join('、')
    return `缺少前置科技：${names}。`
  }

  if (reason === 'already-completed') {
    return '该科技已经完成。'
  }

  return '该科技已经在研究中。'
}

function renderCommandBar(): string {
  const nationId = getViewedNationId()
  const nation = state.nations[nationId]
  const stockpile = nation.economy.stockpile
  const capacity = nation.economy.capacity
  const power = getNationPowerStatus(state, nationId)
  const isRunning = state.status === 'running'

  return `
    <header class="command-bar">
      <div class="command-brand">
        <div>
          <span class="command-brand-mark">SR</span>
          <div>
            <strong>苍穹竞逐</strong>
            <small>${getPatronName(nationId)}</small>
          </div>
        </div>
      </div>

      <div class="command-resources" aria-label="国家资源">
        <span><b>资金</b><strong>${stockpile.money}</strong></span>
        <span><b>金属</b><strong>${stockpile.metal}</strong></span>
        <span><b>燃料</b><strong>${stockpile.fuel}</strong></span>
        <span><b>电子</b><strong>${stockpile.electronics}</strong></span>
        <span class="command-capacity" title="已用电力 / 总电力容量"><b>电力</b><strong>${power.used}/${power.capacity}</strong></span>
        <span class="command-capacity"><b>科研</b><strong>${capacity.research}</strong></span>
      </div>

      <div class="command-turn">
        <span>回合</span>
        <strong>${state.turn}</strong>
        <button id="end-turn" type="button" ${!isRunning || endTurnLocked ? 'disabled' : ''}>
          ${endTurnLocked ? '结算中…' : '结束回合'}
        </button>
      </div>
    </header>
  `
}

function renderNavigation(mobile = false): string {
  const items: Array<{ id: GameView; label: string; short: string }> = [
    { id: 'overview', label: '国家总览', short: '总览' },
    { id: 'map', label: '世界地图', short: '地图' },
    { id: 'research', label: '科技树', short: '科技' },
    { id: 'space', label: '航天计划', short: '航天' },
    { id: 'opponent', label: '对手情报', short: '对手' },
    { id: 'log', label: '战略日志', short: '日志' },
    { id: 'nations', label: '五国档案', short: '档案' },
    { id: 'settings', label: '设置与存档', short: '设置' },
  ]

  return items.map((item) => `
    <button
      type="button"
      data-view="${item.id}"
      class="nav-button ${activeView === item.id ? 'is-active' : ''}"
      aria-current="${activeView === item.id ? 'page' : 'false'}"
    >
      <span>${mobile ? item.short : item.label}</span>
    </button>
  `).join('')
}

function renderOverviewView(): string {
  const nationId = getViewedNationId()
  const profile = getNationProfile(nationId)
  const research = state.research[nationId]
  const program = state.spaceProgram[nationId]
  const projects = state.construction.queue.filter(
    (project) => project.nationId === nationId,
  )
  const opponentId = getOpponentMajorPowerId(state.playerNationId)
  const opponent = opponentId ? state.nations[opponentId] : null
  const activeResearch = research.active
    ? getTechnologyDefinition(research.active.technologyId)
    : null
  const activeMission = program.active
    ? getSpaceMissionDefinition(program.active.missionId)
    : null

  return `
    <section class="view-section overview-view" aria-labelledby="overview-title">
      <div class="view-heading">
        <div>
          <p class="kicker">STRATEGIC COMMAND</p>
          <h1 id="overview-title">${getPatronName(nationId)} · 国家总览</h1>
        </div>
        <span class="view-badge">${profile.doctrineName}</span>
      </div>

      <div class="overview-grid">
        <article class="overview-card overview-card-wide">
          <span>国家路线</span>
          <h2>${profile.doctrineName}</h2>
          <p>${profile.summary}</p>
          <div class="overview-tags">
            ${profile.effectLabels.map((effect) => `<i>${effect}</i>`).join('')}
          </div>
          <button type="button" data-view="nations">查看五国国家档案</button>
        </article>

        <article class="overview-card">
          <span>当前科研</span>
          <h2>${activeResearch?.name ?? '暂无项目'}</h2>
          <p>${research.active
            ? `${research.active.progress} / ${activeResearch?.researchCost ?? 0} 研究点`
            : `已完成 ${research.completed.length}/${TECHNOLOGY_DEFINITIONS.length} 项科技`}</p>
          <button type="button" data-view="research">打开科技树</button>
        </article>

        <article class="overview-card">
          <span>工业建设</span>
          <h2>${projects.length}/${PROTOTYPE_CONSTRUCTION_CAP} 项施工中</h2>
          <p>${projects.length === 0
            ? '当前没有在建工程。'
            : projects.map((project) => {
                const region = getPrototypeRegion(project.regionId)
                return `${region?.name ?? project.regionId} · ${getFacilityDefinition(project.facilityId).name}（${project.turnsRemaining}）`
              }).join('；')}</p>
          <button type="button" data-view="map">前往地图建设</button>
        </article>

        <article class="overview-card">
          <span>航天计划</span>
          <h2>${activeMission?.shortName ?? '待命'}</h2>
          <p>${program.active
            ? `剩余 ${program.active.turnsRemaining}/${program.active.totalTurns} 回合`
            : `里程碑 ${program.completed.length}/${SPACE_MISSION_DEFINITIONS.length}`}</p>
          <button type="button" data-view="space">查看航天计划</button>
        </article>

        <article class="overview-card">
          <span>对手态势</span>
          <h2>${opponentId ? getPatronName(opponentId) : '—'}</h2>
          <p>${opponent
            ? `资金 ${opponent.economy.stockpile.money} · 科研 ${opponent.economy.capacity.research}`
            : '尚未开始原型局。'}</p>
          <button type="button" data-view="opponent">查看对手情报</button>
        </article>

        <article class="overview-card">
          <span>游戏状态</span>
          <h2>${state.status === 'running' ? '竞赛进行中' : state.status === 'won' ? '航天胜利' : state.status === 'lost' ? '竞赛失败' : '尚未开始'}</h2>
          <p>${state.winnerNationId
            ? `${getPatronName(state.winnerNationId)} 已完成最终登月目标。`
            : `当前第 ${state.turn} 回合 · 对手强度 ${getAiDifficultyLabel(state.settings.aiDifficulty)}`}</p>
          <button type="button" data-view="settings">设置与存档</button>
        </article>
      </div>
    </section>
  `
}

function renderLastTurnReport(): string {
  const nationId = state.playerNationId

  if (nationId === null || state.turn <= 1) {
    return ''
  }

  const resolvedTurn = state.turn - 1
  const opponentId = getOpponentMajorPowerId(nationId)
  const income = state.nations[nationId].economy.lastIncome
  const researchPoints = state.nations[nationId].economy.capacity.research
  const turnEvents = state.strategicLog.entries.filter(
    (event) => event.turn === resolvedTurn,
  )
  const playerCompletions = turnEvents.filter(
    (event) =>
      event.nationId === nationId &&
      (
        event.type === 'construction-complete' ||
        event.type === 'research-complete' ||
        event.type === 'space-mission-complete' ||
        event.type === 'victory'
      ),
  )
  const opponentUpdates = opponentId === null
    ? []
    : turnEvents.filter(
        (event) =>
          event.nationId === opponentId &&
          (
            event.type === 'research-start' ||
            event.type === 'construction-start' ||
            event.type === 'space-mission-start' ||
            event.type === 'research-complete' ||
            event.type === 'construction-complete' ||
            event.type === 'space-mission-complete' ||
            event.type === 'victory'
          ),
      )

  const completionText =
    playerCompletions.length === 0
      ? '本回合没有项目完成'
      : playerCompletions.map(formatStrategicEvent).join('；')
  const opponentText =
    opponentUpdates.length === 0
      ? '本回合没有新的重大动态'
      : opponentUpdates.map(formatStrategicEvent).join('；')

  return `
    <section class="turn-report" aria-labelledby="turn-report-title">
      <div class="turn-report-heading">
        <span>TURN REPORT</span>
        <strong id="turn-report-title">第 ${resolvedTurn} 回合结算</strong>
      </div>

      <div class="turn-report-income" aria-label="本回合收益">
        <span>资金 <b>+${income.money}</b></span>
        <span>金属 <b>+${income.metal}</b></span>
        <span>燃料 <b>+${income.fuel}</b></span>
        <span>电子 <b>+${income.electronics}</b></span>
        <span>科研 <b>${researchPoints}/回合</b></span>
      </div>

      <div class="turn-report-events">
        <div>
          <span>本国完成</span>
          <strong>${completionText}</strong>
        </div>
        <div>
          <span>对手动态</span>
          <strong>${opponentText}</strong>
        </div>
      </div>
    </section>
  `
}

function renderMapView(): string {
  const selectedRegion =
    getWorldMapRegion(selectedRegionId) ?? WORLD_MAP_REGIONS[0]

  return `
    <section class="view-section map-view" aria-labelledby="map-view-title">
      <div class="view-heading compact-view-heading">
        <div>
          <p class="kicker">WORLD MAP</p>
          <h1 id="map-view-title">世界地图与地区建设</h1>
        </div>
        <span class="view-badge">已选择：${selectedRegion.name}</span>
      </div>

      ${renderLastTurnReport()}

      <div class="map-workspace">
        <div class="map-card">
          ${renderMap()}
          <div class="map-legend" aria-label="地图图例">
            <span><i class="legend-swatch legend-starsea"></i>阿美丽卡</span>
            <span><i class="legend-swatch legend-redplain"></i>苏维埃</span>
            <span><i class="legend-swatch legend-zhonghua"></i>中华</span>
            <span><i class="legend-swatch legend-britannia"></i>英吉利</span>
            <span><i class="legend-swatch legend-francia"></i>法兰西</span>
            <span><i class="legend-swatch legend-neutral"></i>独立小国</span>
            <span><i class="legend-client"></i>附属国</span>
            <span><i class="legend-planned"></i>未来规划区</span>
          </div>
        </div>
        ${renderRegionDetails(selectedRegion)}
      </div>
    </section>
  `
}

function renderNationArchiveView(): string {
  const entries = getNationArchiveEntries()
  const selected = getNationArchiveEntry(selectedArchiveNationId)
  const availabilityLabel =
    selected.availability === 'active'
      ? '当前可玩'
      : '未来可玩 · 尚未激活'
  const focusBranches = selected.focusBranches
    .map((branch) => getTechnologyBranchLabel(branch))
    .join(' · ')
  const preferredTechnologies = selected.preferredTechnologies
    .map((technologyId) => getTechnologyDefinition(technologyId).name)
    .join('、')
  const facilityPriorities = selected.facilityPriorities
    .map((facilityId) => getFacilityDefinition(facilityId).name)
    .join(' → ')
  const researchPriorities = selected.researchPriorities
    .slice(0, 5)
    .map((technologyId) => getTechnologyDefinition(technologyId).name)
    .join(' → ')
  const missionAdvantages = selected.missionModifiers
    .map(({ missionId, turns }) =>
      `${getSpaceMissionDefinition(missionId).shortName} ${turns} 回合`,
    )
    .join(' · ')

  return `
    <section class="view-section nation-archive-view" aria-labelledby="nation-archive-title">
      <div class="view-heading">
        <div>
          <p class="kicker">FIVE-POWER ARCHIVE</p>
          <h1 id="nation-archive-title">五国国家档案</h1>
        </div>
        <span class="view-badge">2 国当前可玩 · 3 国未来可玩</span>
      </div>

      <div class="nation-archive-layout">
        <aside class="nation-archive-list" aria-label="五国档案列表">
          ${entries.map((entry) => `
            <button
              type="button"
              data-archive-nation="${entry.id}"
              class="nation-archive-tab ${entry.id === selected.id ? 'is-active' : ''}"
              aria-pressed="${entry.id === selected.id ? 'true' : 'false'}"
            >
              <span>${entry.displayName}</span>
              <small>${entry.availability === 'active' ? '当前可玩' : '未来可玩'}</small>
            </button>
          `).join('')}
        </aside>

        <article class="nation-archive-detail">
          <header class="nation-archive-hero">
            <div>
              <span class="nation-availability ${selected.availability === 'active' ? 'is-active' : 'is-planned'}">
                ${availabilityLabel}
              </span>
              <h2>${selected.displayName}</h2>
              <strong>${selected.routeName}</strong>
              <p>${selected.doctrineSummary}</p>
            </div>
            <div class="nation-archive-code">
              <span>国家 ID</span>
              <code>${selected.id}</code>
            </div>
          </header>

          <section class="nation-archive-traits">
            <div class="archive-section-heading">
              <span>国家路线特性</span>
              <small>配置来自统一国家数据源</small>
            </div>
            <div class="nation-archive-trait-grid">
              ${selected.effectLabels.map((effect) => `<span>${effect}</span>`).join('')}
            </div>
          </section>

          <div class="nation-archive-info-grid">
            <section>
              <span>科研方向</span>
              <strong>${focusBranches}</strong>
              <p>偏好科技：${preferredTechnologies}</p>
            </section>
            <section>
              <span>设施倾向</span>
              <strong>${facilityPriorities}</strong>
              <p>AI 会依据地图条件把这套优先级映射到可建设地区。</p>
            </section>
            <section>
              <span>航天倾向</span>
              <strong>${missionAdvantages || '无额外准备时间修正'}</strong>
              <p>任务仍遵守同一套科技、前置里程碑和资源成本。</p>
            </section>
            <section>
              <span>AI 风格</span>
              <strong>${selected.aiEnabled ? '当前剧本已启用' : '未来策略配置已准备'}</strong>
              <p>科研优先：${researchPriorities}</p>
            </section>
          </div>

          <div class="nation-archive-status-note ${selected.availability === 'active' ? 'is-active' : 'is-planned'}">
            <strong>${selected.availability === 'active' ? '当前原型局已启用' : '当前原型局暂未启用'}</strong>
            <span>${selected.availability === 'active'
              ? '该国家已经参与现有地图、回合、AI 与航天竞赛。'
              : '路线、科技倾向和 AI 配置已经完成，但不会提前进入现有两国地图或回合。'}</span>
          </div>
        </article>
      </div>
    </section>
  `
}

function renderSettingsView(): string {
  const isRunning = state.status === 'running'

  return `
    <section class="view-section settings-view" aria-labelledby="settings-title">
      <div class="view-heading">
        <div>
          <p class="kicker">GAME SETTINGS</p>
          <h1 id="settings-title">新游戏、难度与存档</h1>
        </div>
        <span class="view-badge">${PROJECT_PHASE}</span>
      </div>

      <div class="settings-grid">
        <article class="settings-card">
          <h2>新游戏设置</h2>
          <label>
            <span>玩家国家</span>
            <select id="start-nation">
              <option value="starsea" ${selectedStartNationId === 'starsea' ? 'selected' : ''}>阿美丽卡联邦</option>
              <option value="redplain" ${selectedStartNationId === 'redplain' ? 'selected' : ''}>苏维埃联盟</option>
            </select>
          </label>
          <label>
            <span>对手强度</span>
            <select id="ai-difficulty">
              <option value="relaxed" ${selectedAiDifficulty === 'relaxed' ? 'selected' : ''}>宽松</option>
              <option value="standard" ${selectedAiDifficulty === 'standard' ? 'selected' : ''}>标准</option>
              <option value="intense" ${selectedAiDifficulty === 'intense' ? 'selected' : ''}>高压</option>
            </select>
          </label>
          <button id="new-game" class="primary-action" type="button">
            ${isRunning ? '重新开始原型局' : '开始原型局'}
          </button>
        </article>

        <article class="settings-card">
          <h2>存档</h2>
          <p>自动存档会继续使用浏览器本地存储。导出文件可用于手动备份或跨浏览器迁移。</p>
          <div class="settings-actions">
            <button id="export-save" type="button">导出存档</button>
            <button id="import-save" type="button">导入存档</button>
            <input id="save-file" class="visually-hidden" type="file" accept=".json,application/json" />
          </div>
        </article>

        <article class="settings-card">
          <h2>当前版本</h2>
          <dl>
            <div><dt>阶段</dt><dd>${PROJECT_PHASE}</dd></div>
            <div><dt>版本</dt><dd>${GAME_VERSION}</dd></div>
            <div><dt>存档结构</dt><dd>Schema v${state.schemaVersion}</dd></div>
            <div><dt>当前国家</dt><dd>${state.playerNationId ? getPatronName(state.playerNationId) : '尚未开始'}</dd></div>
          </dl>
        </article>
      </div>
    </section>
  `
}

function renderActiveView(): string {
  if (activeView === 'map') {
    return renderMapView()
  }

  if (activeView === 'research') {
    return `<section class="view-section standalone-system">${renderResearch()}</section>`
  }

  if (activeView === 'space') {
    return `<section class="view-section standalone-system">${renderSpaceProgram()}</section>`
  }

  if (activeView === 'opponent') {
    return `<section class="view-section standalone-system">${renderOpponentIntel()}</section>`
  }

  if (activeView === 'log') {
    return `<section class="view-section standalone-system">${renderStrategicLog()}</section>`
  }

  if (activeView === 'nations') {
    return renderNationArchiveView()
  }

  if (activeView === 'settings') {
    return renderSettingsView()
  }

  return renderOverviewView()
}

function isGameView(value: unknown): value is GameView {
  return typeof value === 'string' && GAME_VIEWS.includes(value as GameView)
}

function render(): void {
  app.innerHTML = `
    <section class="game-shell" aria-label="苍穹竞逐战略指挥界面">
      ${renderCommandBar()}

      <div class="command-workspace">
        <aside class="side-navigation" aria-label="游戏主导航">
          <div class="side-navigation-head">
            <span>SKYWARD RACE</span>
            <small>${PROJECT_PHASE}</small>
          </div>
          <nav>
            ${renderNavigation(false)}
          </nav>
        </aside>

        <main class="workspace-main" id="workspace-main" tabindex="-1">
          ${renderActiveView()}
        </main>
      </div>

      <footer class="command-status notice-${noticeTone}" role="status">
        <span>${notice}</span>
        <small>${GAME_VERSION} · Schema v${state.schemaVersion}</small>
      </footer>

      <nav class="mobile-navigation" aria-label="移动端主导航">
        ${renderNavigation(true)}
      </nav>
    </section>
  `

  bindUi()
}

function bindUi(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-archive-nation]').forEach((button) => {
    button.addEventListener('click', () => {
      const nationId = button.dataset.archiveNation
      if (!isMajorPowerId(nationId)) {
        return
      }

      selectedArchiveNationId = nationId
      render()
      document.querySelector<HTMLElement>('#workspace-main')?.focus({ preventScroll: true })
    })
  })

  document.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((button) => {
    button.addEventListener('click', () => {
      const view = button.dataset.view
      if (!isGameView(view)) {
        return
      }

      activeView = view
      render()
      document.querySelector<HTMLElement>('#workspace-main')?.focus({ preventScroll: true })
    })
  })

  document.querySelectorAll<SVGGElement>('[data-region-id]').forEach((element) => {
    const selectRegion = (): void => {
      const regionId = element.dataset.regionId
      if (!regionId || !getWorldMapRegion(regionId)) {
        return
      }

      selectedRegionId = regionId
      render()
    }

    element.addEventListener('click', selectRegion)
    element.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        selectRegion()
      }
    })
  })

  document.querySelectorAll<HTMLButtonElement>('[data-build-facility]').forEach(
    (button) => {
      button.addEventListener('click', () => {
        const nationId = state.playerNationId
        const rawFacilityId = button.dataset.buildFacility

        if (
          nationId === null ||
          !rawFacilityId ||
          !FACILITY_DEFINITIONS.some(
            (facility) => facility.id === rawFacilityId,
          )
        ) {
          return
        }

        const facilityId = rawFacilityId as FacilityId
        const result = queueFacilityConstruction(
          state,
          nationId,
          selectedRegionId,
          facilityId,
        )

        if (!result.queued) {
          notice = constructionReasonText(result.reason)
          noticeTone = 'error'
          render()
          return
        }

        state = {
          ...result.state,
          strategicLog: appendStrategicEvents(
            result.state.strategicLog,
            [{
              turn: result.state.turn,
              nationId,
              type: 'construction-start',
              regionId: selectedRegionId,
              facilityId,
            }],
          ),
        }
        const facility = getFacilityDefinition(facilityId)
        persistState(
          `${facility.name}已在当前地区开工，预计 ${facility.buildTurns} 回合完成。`,
        )
        render()
      })
    },
  )

  document.querySelectorAll<HTMLButtonElement>('[data-space-mission]').forEach(
    (button) => {
      button.addEventListener('click', () => {
        const nationId = state.playerNationId
        const rawMissionId = button.dataset.spaceMission

        if (
          nationId === null ||
          !rawMissionId ||
          !isSpaceMissionId(rawMissionId)
        ) {
          return
        }

        const nation = state.nations[nationId]
        const result = startSpaceMission(
          nationId,
          state.spaceProgram[nationId],
          state.research[nationId],
          nation.economy.stockpile,
          rawMissionId,
          state.status === 'running',
        )

        if (!result.started) {
          notice = spaceMissionReasonText(
            result.reason,
            result.missingTechnologies,
            result.missingMilestone,
          )
          noticeTone = 'error'
          render()
          return
        }

        const missionState = {
          ...state,
          nations: {
            ...state.nations,
            [nationId]: {
              ...nation,
              economy: {
                ...nation.economy,
                stockpile: result.stockpile,
              },
            },
          },
          spaceProgram: {
            ...state.spaceProgram,
            [nationId]: result.program,
          },
        }
        state = {
          ...missionState,
          strategicLog: appendStrategicEvents(
            missionState.strategicLog,
            [{
              turn: missionState.turn,
              nationId,
              type: 'space-mission-start',
              missionId: rawMissionId,
            }],
          ),
        }

        persistState(
          `航天任务“${getSpaceMissionDefinition(rawMissionId).name}”已启动。`,
        )
        render()
      })
    },
  )

  document.querySelectorAll<HTMLButtonElement>('[data-research-tech]').forEach(
    (button) => {
      button.addEventListener('click', () => {
        const nationId = state.playerNationId
        const rawTechnologyId = button.dataset.researchTech

        if (
          nationId === null ||
          !rawTechnologyId ||
          !isTechnologyId(rawTechnologyId)
        ) {
          return
        }

        const result = startResearch(
          state.research[nationId],
          rawTechnologyId,
        )

        if (!result.started) {
          notice = researchReasonText(
            result.reason,
            result.missingPrerequisites,
          )
          noticeTone = 'error'
          render()
          return
        }

        const researchState = {
          ...state,
          research: {
            ...state.research,
            [nationId]: result.state,
          },
        }
        state = {
          ...researchState,
          strategicLog: appendStrategicEvents(
            researchState.strategicLog,
            [{
              turn: researchState.turn,
              nationId,
              type: 'research-start',
              technologyId: rawTechnologyId,
            }],
          ),
        }
        persistState(
          `已开始研究“${getTechnologyDefinition(rawTechnologyId).name}”。`,
        )
        render()
      })
    },
  )

  document.querySelector<HTMLSelectElement>('#start-nation')?.addEventListener(
    'change',
    (event) => {
      const value = (event.currentTarget as HTMLSelectElement).value
      if (value === 'starsea' || value === 'redplain') {
        selectedStartNationId = value
      }
    },
  )

  document.querySelector<HTMLSelectElement>('#ai-difficulty')?.addEventListener(
    'change',
    (event) => {
      const value = (event.currentTarget as HTMLSelectElement).value
      if (isAiDifficulty(value)) {
        selectedAiDifficulty = value
      }
    },
  )

  document.querySelector<HTMLButtonElement>('#new-game')?.addEventListener('click', () => {
    if (state.status === 'running') {
      const confirmed = window.confirm('重新开始会覆盖当前自动存档。确定继续吗？')
      if (!confirmed) {
        return
      }
    }

    state = startNewGame(selectedStartNationId, selectedAiDifficulty)
    selectedRegionId =
      selectedStartNationId === 'starsea' ? 'star-west' : 'red-west'
    activeView = 'map'
    persistState(
      `原型局已开始：${getPatronName(selectedStartNationId)}，对手强度为${getAiDifficultyLabel(selectedAiDifficulty)}。`,
    )
    render()
  })

  document.querySelector<HTMLButtonElement>('#end-turn')?.addEventListener('click', () => {
    if (endTurnLocked) {
      return
    }

    endTurnLocked = true
    const expectedTurn = state.turn
    const result = endTurn(state, expectedTurn)

    if (!result.advanced) {
      endTurnLocked = false
      notice = '当前状态不能结束回合。'
      noticeTone = 'error'
      render()
      return
    }

    state = result.state
    const nationId = getViewedNationId()
    const income = state.nations[nationId].economy.lastIncome
    const researchPoints = state.nations[nationId].economy.capacity.research
    const completedConstructionText =
      result.completedConstruction.length === 0
        ? ''
        : ` 完工：${result.completedConstruction.map((project) =>
            getFacilityDefinition(project.facilityId).name,
          ).join('、')}。`
    const completedResearch = result.completedResearch
      .filter((completion) => completion.nationId === nationId)
      .map((completion) =>
        getTechnologyDefinition(completion.technologyId).name,
      )
    const completedResearchText =
      completedResearch.length === 0
        ? ''
        : ` 科技完成：${completedResearch.join('、')}。`
    const completedSpaceMissions = result.completedSpaceMissions
      .filter((completion) => completion.nationId === nationId)
      .map((completion) =>
        getSpaceMissionDefinition(completion.missionId).name,
      )
    const completedSpaceMissionText =
      completedSpaceMissions.length === 0
        ? ''
        : ` 航天里程碑完成：${completedSpaceMissions.join('、')}。`
    const victoryText =
      state.status === 'won'
        ? ' 已完成载人登月并安全返回，取得航天胜利。'
        : state.status === 'lost'
          ? ' 对手已率先完成载人登月并安全返回。'
          : ''
    const opponentActionText =
      result.aiActions.length === 0
        ? ''
        : ` 对手行动：${result.aiActions.map((action) => {
            if (action.type === 'research-start') {
              return `研究${getTechnologyDefinition(action.technologyId).name}`
            }
            if (action.type === 'construction-start') {
              const region = getPrototypeRegion(action.regionId)
              return `${region?.name ?? action.regionId}建设${getFacilityDefinition(action.facilityId).name}`
            }
            return `启动${getSpaceMissionDefinition(action.missionId).shortName}`
          }).join('；')}。`

    persistState(
      `第 ${expectedTurn} 回合完成：资金 +${income.money}，金属 +${income.metal}，燃料 +${income.fuel}，电子元件 +${income.electronics}；科研推进最多 ${researchPoints} 点。${completedConstructionText}${completedResearchText}${completedSpaceMissionText}${opponentActionText}${victoryText}`,
    )
    render()

    window.setTimeout(() => {
      endTurnLocked = false
      render()
    }, 250)
  })

  document.querySelector<HTMLButtonElement>('#export-save')?.addEventListener('click', () => {
    try {
      downloadGameSave(state)
      notice = `已导出第 ${state.turn} 回合存档。`
      noticeTone = 'success'
    } catch {
      notice = '导出失败，请稍后重试。'
      noticeTone = 'error'
    }

    render()
  })

  const fileInput = document.querySelector<HTMLInputElement>('#save-file')

  document.querySelector<HTMLButtonElement>('#import-save')?.addEventListener('click', () => {
    fileInput?.click()
  })

  fileInput?.addEventListener('change', async () => {
    const file = fileInput.files?.[0]

    if (!file) {
      return
    }

    try {
      const importedState = await readGameSaveFile(file)
      state = importedState
      selectedStartNationId = state.playerNationId ?? 'starsea'
      selectedAiDifficulty = state.settings.aiDifficulty
      activeView = 'map'
      persistState(`存档导入成功，当前为第 ${state.turn} 回合。`)
    } catch (error) {
      notice = error instanceof Error ? error.message : '导入失败。'
      noticeTone = 'error'
    } finally {
      fileInput.value = ''
      render()
    }
  })
}
